export interface PersianQuestion {
  id: number;
  persian: string;
  pronunciation: string;
  english: string;
  audioUrl?: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

export interface Lesson {
  _id?: string;
  id?: string | number;
  title: string;
  persianTitle: string;
  description: string;
  focus: string;
  goal: string;
  icon: string;
  questions: PersianQuestion[];
  quiz: QuizQuestion[];
}

export interface QuizQuestion {
  id: number;
  question: string;
  type: 'multiple-choice' | 'translation' | 'pronunciation';
  options?: string[];
  correctAnswer: string;
  explanation: string;
}

export const persianQuestionsData: Lesson[] = [
  {
    id: 1,
    title: "Getting to Know You",
    persianTitle: "آشنایی اولیه",
    description: "Learn to ask and answer basic personal questions",
    focus: "Introductions & personal background",
    goal: "Be able to ask and answer basic personal questions",
    icon: "🧑‍🤝‍🧑",
    questions: [
      {
        id: 1,
        persian: "اسمت چیه؟",
        pronunciation: "Esmet chiye?",
        english: "What's your name?",
        category: "introduction",
        difficulty: "beginner",
        audioUrl: "/audio/whats_your_name.wav"
      },
      {
        id: 2,
        persian: "اهل کجایی؟",
        pronunciation: "Ahl-e kojāyi?",
        english: "Where are you from?",
        category: "introduction",
        difficulty: "beginner",
        audioUrl: "/audio/where_are_you_from.wav"
      },
      {
        id: 3,
        persian: "چند سالته؟",
        pronunciation: "Chand sālate?",
        english: "How old are you?",
        category: "introduction",
        difficulty: "beginner",
        audioUrl: "/audio/how_old_are_you.wav"
      },
      {
        id: 4,
        persian: "چه کار می‌کنی؟",
        pronunciation: "Che kār mikoni?",
        english: "What do you do?",
        category: "introduction",
        difficulty: "beginner",
        audioUrl: "/audio/what_do_you_do.wav"
      },
      {
        id: 5,
        persian: "با کی اومدی؟",
        pronunciation: "Bā ki umadi?",
        english: "Who did you come with?",
        category: "introduction",
        difficulty: "beginner",
        audioUrl: "/audio/who_did_you_come_with.wav"
      }
    ],
    quiz: [
      {
        id: 1,
        question: "How do you ask 'What's your name?' in Persian?",
        type: "multiple-choice",
        options: [
          "اهل کجایی؟",
          "اسمت چیه؟",
          "چند سالته؟",
          "چه کار می‌کنی؟"
        ],
        correctAnswer: "اسمت چیه؟",
        explanation: "اسمت چیه؟ (Esmet chiye?) is the correct way to ask 'What's your name?'"
      },
      {
        id: 2,
        question: "What does 'اهل کجایی؟' mean?",
        type: "translation",
        correctAnswer: "Where are you from?",
        explanation: "اهل کجایی؟ (Ahl-e kojāyi?) means 'Where are you from?'"
      },
      {
        id: 3,
        question: "Translate: 'What do you do?'",
        type: "translation",
        correctAnswer: "چه کار می‌کنی؟",
        explanation: "چه کار می‌کنی؟ (Che kār mikoni?) means 'What do you do?'"
      }
    ]
  },
  {
    id: 2,
    title: "Time & Place",
    persianTitle: "زمان و مکان",
    description: "Learn to talk about time, location, and arrival",
    focus: "Talking about time, location, and arrival",
    goal: "Understand and ask for basic situational info",
    icon: "🕰️",
    questions: [
      {
        id: 6,
        persian: "ساعت چنده؟",
        pronunciation: "Sā'at chande?",
        english: "What time is it?",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/what_time_is_it.wav"
      },
      {
        id: 7,
        persian: "امروز چه روزیه؟",
        pronunciation: "Emrooz che roozie?",
        english: "What day is it today?",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/what_day_is_it_today.wav"
      },
      {
        id: 8,
        persian: "الان کجایی؟",
        pronunciation: "Alān kojāyi?",
        english: "Where are you now?",
        category: "location",
        difficulty: "beginner",
        audioUrl: "/audio/where_are_you_now.wav"
      },
      {
        id: 9,
        persian: "کی می‌رسیم؟",
        pronunciation: "Key miresim?",
        english: "When do we arrive?",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/when_do_we_arrive.wav"
      },
      {
        id: 10,
        persian: "چه‌قدر طول می‌کشه؟",
        pronunciation: "Cheghadr tool mikeshe?",
        english: "How long does it take?",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/how_long_does_it_take.wav"
      }
    ],
    quiz: [
      {
        id: 4,
        question: "How do you ask 'What time is it?' in Persian?",
        type: "multiple-choice",
        options: [
          "امروز چه روزیه؟",
          "ساعت چنده؟",
          "الان کجایی؟",
          "کی می‌رسیم؟"
        ],
        correctAnswer: "ساعت چنده؟",
        explanation: "ساعت چنده؟ (Sā'at chande?) is the correct way to ask 'What time is it?'"
      },
      {
        id: 5,
        question: "What does 'امروز چه روزیه؟' mean?",
        type: "translation",
        correctAnswer: "What day is it today?",
        explanation: "امروز چه روزیه؟ (Emrooz che roozie?) means 'What day is it today?'"
      },
      {
        id: 6,
        question: "Translate: 'How long does it take?'",
        type: "translation",
        correctAnswer: "چه‌قدر طول می‌کشه؟",
        explanation: "چه‌قدر طول می‌کشه؟ (Cheghadr tool mikeshe?) means 'How long does it take?'"
      }
    ]
  },
  {
    id: 3,
    title: "Food & Shopping",
    persianTitle: "غذا و خرید",
    description: "Learn to order food, shop, and handle payments",
    focus: "Ordering food, shopping, and payments",
    goal: "Manage restaurant and market situations",
    icon: "🍽️",
    questions: [
      {
        id: 11,
        persian: "این چنده؟",
        pronunciation: "In chande?",
        english: "How much is this?",
        category: "shopping",
        difficulty: "beginner",
        audioUrl: "/audio/how_much_is_this.wav"
      },
      {
        id: 12,
        persian: "رستوران خوب کجاست؟",
        pronunciation: "Resturān-e khoob kojāst?",
        english: "Where's a good restaurant?",
        category: "food",
        difficulty: "beginner",
        audioUrl: "/audio/where_is_a_good_restaurant.wav"
      },
      {
        id: 13,
        persian: "چی سفارش دادی؟",
        pronunciation: "Chi sefāresh dādi?",
        english: "What did you order?",
        category: "food",
        difficulty: "beginner",
        audioUrl: "/audio/what_did_you_order.wav"
      },
      {
        id: 14,
        persian: "غذاش تنده؟",
        pronunciation: "Ghāzāsh tonde?",
        english: "Is it spicy?",
        category: "food",
        difficulty: "beginner",
        audioUrl: "/audio/is_it_spicy.wav"
      },
      {
        id: 15,
        persian: "نقدی یا کارت؟",
        pronunciation: "Naqdī yā kārt?",
        english: "Cash or card?",
        category: "payment",
        difficulty: "beginner",
        audioUrl: "/audio/cash_or_card.wav"
      }
    ],
    quiz: [
      {
        id: 7,
        question: "How do you ask 'How much is this?' in Persian?",
        type: "multiple-choice",
        options: [
          "این چنده؟",
          "رستوران خوب کجاست؟",
          "چی سفارش دادی؟",
          "غذاش تنده؟"
        ],
        correctAnswer: "این چنده؟",
        explanation: "این چنده؟ (In chande?) is the correct way to ask 'How much is this?'"
      },
      {
        id: 8,
        question: "What does 'غذاش تنده؟' mean?",
        type: "translation",
        correctAnswer: "Is it spicy?",
        explanation: "غذاش تنده؟ (Ghāzāsh tonde?) means 'Is it spicy?'"
      },
      {
        id: 9,
        question: "Translate: 'Cash or card?'",
        type: "translation",
        correctAnswer: "نقدی یا کارت؟",
        explanation: "نقدی یا کارت؟ (Naqdī yā kārt?) means 'Cash or card?'"
      }
    ]
  },
  {
    id: 4,
    title: "Conversation Essentials",
    persianTitle: "مکالمه‌ی روزمره",
    description: "Learn listening and speaking support phrases",
    focus: "Listening and speaking support",
    goal: "Understand fast talkers and respond naturally",
    icon: "💬",
    questions: [
      {
        id: 16,
        persian: "شماره‌تو داری؟",
        pronunciation: "Shomāreto dāri?",
        english: "Do you have your number?",
        category: "communication",
        difficulty: "beginner",
        audioUrl: "/audio/do_you_have_your_number.wav"
      },
      {
        id: 17,
        persian: "میشه دوباره بگی؟",
        pronunciation: "Mishe dobare begi?",
        english: "Can you say that again?",
        category: "communication",
        difficulty: "beginner",
        audioUrl: "/audio/can_you_say_that_again.wav"
      },
      {
        id: 18,
        persian: "یواش‌تر لطفاً!",
        pronunciation: "Yavāshtar lotfan!",
        english: "More slowly, please!",
        category: "communication",
        difficulty: "beginner",
        audioUrl: "/audio/more_slowly_please.wav"
      },
      {
        id: 19,
        persian: "بلدی فارسی صحبت کنی؟",
        pronunciation: "Baladi Fārsi sohbat koni?",
        english: "Can you speak Farsi?",
        category: "communication",
        difficulty: "beginner",
        audioUrl: "/audio/can_you_speak_farsi.wav"
      },
      {
        id: 20,
        persian: "این یعنی چی؟",
        pronunciation: "In yani chi?",
        english: "What does this mean?",
        category: "communication",
        difficulty: "beginner",
        audioUrl: "/audio/what_does_this_mean.wav"
      }
    ],
    quiz: [
      {
        id: 10,
        question: "How do you ask 'Can you say that again?' in Persian?",
        type: "multiple-choice",
        options: [
          "شماره‌تو داری؟",
          "میشه دوباره بگی؟",
          "یواش‌تر لطفاً!",
          "بلدی فارسی صحبت کنی؟"
        ],
        correctAnswer: "میشه دوباره بگی؟",
        explanation: "میشه دوباره بگی؟ (Mishe dobare begi?) is the correct way to ask 'Can you say that again?'"
      },
      {
        id: 11,
        question: "What does 'یواش‌تر لطفاً!' mean?",
        type: "translation",
        correctAnswer: "More slowly, please!",
        explanation: "یواش‌تر لطفاً! (Yavāshtar lotfan!) means 'More slowly, please!'"
      },
      {
        id: 12,
        question: "Translate: 'What does this mean?'",
        type: "translation",
        correctAnswer: "این یعنی چی؟",
        explanation: "این یعنی چی؟ (In yani chi?) means 'What does this mean?'"
      }
    ]
  },
  {
    id: 5,
    title: "Culture & Connection",
    persianTitle: "فرهنگ و ارتباط",
    description: "Learn about Persian culture, customs, and deeper questions",
    focus: "Persian culture, customs, and deeper questions",
    goal: "Feel confident engaging in cultural exchange",
    icon: "🎉",
    questions: [
      {
        id: 21,
        persian: "عیدت مبارک!",
        pronunciation: "Eidet mobārak!",
        english: "Happy holiday!",
        category: "culture",
        difficulty: "beginner",
        audioUrl: "/audio/happy_holiday.wav"
      },
      {
        id: 22,
        persian: "تو کجای ایران بودی؟",
        pronunciation: "To kojāye Irān boodi?",
        english: "Where in Iran are you from?",
        category: "culture",
        difficulty: "beginner",
        audioUrl: "/audio/where_in_iran_are_you_from.wav"
      },
      {
        id: 23,
        persian: "شعر حافظ بلدی؟",
        pronunciation: "She'r-e Hāfez baladi?",
        english: "Do you know Hafez poetry?",
        category: "culture",
        difficulty: "beginner",
        audioUrl: "/audio/do_you_know_hafez_poetry.wav"
      },
      {
        id: 24,
        persian: "کجا درس خوندی؟",
        pronunciation: "Kojā dars khundi?",
        english: "Where did you study?",
        category: "education",
        difficulty: "beginner",
        audioUrl: "/audio/where_did_you_study.wav"
      },
      {
        id: 25,
        persian: "دوست داری بری ایران؟",
        pronunciation: "Doost dāri beri Irān?",
        english: "Do you want to go to Iran?",
        category: "culture",
        difficulty: "beginner",
        audioUrl: "/audio/do_you_want_to_go_to_iran.wav"
      }
    ],
    quiz: [
      {
        id: 13,
        question: "How do you say 'Happy holiday!' in Persian?",
        type: "multiple-choice",
        options: [
          "عیدت مبارک!",
          "تو کجای ایران بودی؟",
          "شعر حافظ بلدی؟",
          "کجا درس خوندی؟"
        ],
        correctAnswer: "عیدت مبارک!",
        explanation: "عیدت مبارک! (Eidet mobārak!) is the correct way to say 'Happy holiday!'"
      },
      {
        id: 14,
        question: "What does 'شعر حافظ بلدی؟' mean?",
        type: "translation",
        correctAnswer: "Do you know Hafez poetry?",
        explanation: "شعر حافظ بلدی؟ (She'r-e Hāfez baladi?) means 'Do you know Hafez poetry?'"
      },
      {
        id: 15,
        question: "Translate: 'Do you want to go to Iran?'",
        type: "translation",
        correctAnswer: "دوست داری بری ایران؟",
        explanation: "دوست داری بری ایران؟ (Doost dāri beri Irān?) means 'Do you want to go to Iran?'"
      }
    ]
  },
  {
    id: 6,
    title: "Tick Tock Farsi: Learn Persian Months, Days & Numbers",
    persianTitle: "زمان‌شناسی فارسی: ماه‌ها، روزها و اعداد",
    description: "Master Persian time vocabulary including months, days of the week, numbers 1-100, seasons, and time expressions",
    focus: "Persian calendar system, time vocabulary, numerical expressions, and temporal language",
    goal: "Confidently discuss dates, times, make appointments, and use numbers in everyday Persian conversation",
    icon: "🐣",
    questions: [
      // Mini-Course 1: Persian Numbers for Time (1–100)
      {
        id: 26,
        persian: "یک",
        pronunciation: "Yek",
        english: "One",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/one.wav"
      },
      {
        id: 27,
        persian: "دو",
        pronunciation: "Do",
        english: "Two",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/two.wav"
      },
      {
        id: 28,
        persian: "سه",
        pronunciation: "Se",
        english: "Three",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/three.wav"
      },
      {
        id: 29,
        persian: "چهار",
        pronunciation: "Chahār",
        english: "Four",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/four.wav"
      },
      {
        id: 30,
        persian: "پنج",
        pronunciation: "Panj",
        english: "Five",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/five.wav"
      },
      {
        id: 31,
        persian: "شش",
        pronunciation: "Shesh",
        english: "Six",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/six.wav"
      },
      {
        id: 32,
        persian: "هفت",
        pronunciation: "Haft",
        english: "Seven",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/seven.wav"
      },
      {
        id: 33,
        persian: "هشت",
        pronunciation: "Hasht",
        english: "Eight",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/eight.wav"
      },
      {
        id: 34,
        persian: "نه",
        pronunciation: "Noh",
        english: "Nine",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/nine.wav"
      },
      {
        id: 35,
        persian: "ده",
        pronunciation: "Dah",
        english: "Ten",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/ten.wav"
      },
      {
        id: 36,
        persian: "یازده",
        pronunciation: "Yāzdah",
        english: "Eleven",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/eleven.wav"
      },
      {
        id: 37,
        persian: "دوازده",
        pronunciation: "Davāzdah",
        english: "Twelve",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/twelve.wav"
      },
      {
        id: 38,
        persian: "سیزده",
        pronunciation: "Sizdah",
        english: "Thirteen",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/thirteen.wav"
      },
      {
        id: 39,
        persian: "چهارده",
        pronunciation: "Chahārdah",
        english: "Fourteen",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/fourteen.wav"
      },
      {
        id: 40,
        persian: "پانزده",
        pronunciation: "Pānzdah",
        english: "Fifteen",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/fifteen.wav"
      },
      {
        id: 41,
        persian: "شانزده",
        pronunciation: "Shānzdah",
        english: "Sixteen",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/sixteen.wav"
      },
      {
        id: 42,
        persian: "هفده",
        pronunciation: "Hefdah",
        english: "Seventeen",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/seventeen.wav"
      },
      {
        id: 43,
        persian: "هجده",
        pronunciation: "Hejdah",
        english: "Eighteen",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/eighteen.wav"
      },
      {
        id: 44,
        persian: "نوزده",
        pronunciation: "Nuzdah",
        english: "Nineteen",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/nineteen.wav"
      },
      {
        id: 45,
        persian: "بیست",
        pronunciation: "Bist",
        english: "Twenty",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/twenty.wav"
      },
      {
        id: 46,
        persian: "سی",
        pronunciation: "Si",
        english: "Thirty",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/thirty.wav"
      },
      {
        id: 47,
        persian: "چهل",
        pronunciation: "Chehel",
        english: "Forty",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/forty.wav"
      },
      {
        id: 48,
        persian: "پنجاه",
        pronunciation: "Panjāh",
        english: "Fifty",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/fifty.wav"
      },
      {
        id: 49,
        persian: "شصت",
        pronunciation: "Shast",
        english: "Sixty",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/sixty.wav"
      },
      {
        id: 50,
        persian: "هفتاد",
        pronunciation: "Haftād",
        english: "Seventy",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/seventy.wav"
      },
      {
        id: 51,
        persian: "هشتاد",
        pronunciation: "Hashtād",
        english: "Eighty",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/eighty.wav"
      },
      {
        id: 52,
        persian: "نود",
        pronunciation: "Navad",
        english: "Ninety",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/ninety.wav"
      },
      {
        id: 53,
        persian: "صد",
        pronunciation: "Sad",
        english: "One Hundred",
        category: "numbers",
        difficulty: "beginner",
        audioUrl: "/audio/one_hundred.wav"
      },
      {
        id: 54,
        persian: "چند؟",
        pronunciation: "Chand?",
        english: "How many?",
        category: "questions",
        difficulty: "beginner",
        audioUrl: "/audio/how_many.wav"
      },
      {
        id: 55,
        persian: "سال",
        pronunciation: "Sāl",
        english: "Year",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/year.wav"
      },
      {
        id: 56,
        persian: "روز",
        pronunciation: "Rooz",
        english: "Day",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/day.wav"
      },
      {
        id: 57,
        persian: "شماره",
        pronunciation: "Shomāre",
        english: "Number",
        category: "general",
        difficulty: "beginner",
        audioUrl: "/audio/number.wav"
      },
      // Mini-Course 2: Days of the Week & Weekly Routines
      {
        id: 58,
        persian: "شنبه",
        pronunciation: "Shanbe",
        english: "Saturday",
        category: "days",
        difficulty: "beginner",
        audioUrl: "/audio/saturday.wav"
      },
      {
        id: 59,
        persian: "یکشنبه",
        pronunciation: "Yekshanbe",
        english: "Sunday",
        category: "days",
        difficulty: "beginner",
        audioUrl: "/audio/sunday.wav"
      },
      {
        id: 60,
        persian: "دوشنبه",
        pronunciation: "Doshanbe",
        english: "Monday",
        category: "days",
        difficulty: "beginner",
        audioUrl: "/audio/monday.wav"
      },
      {
        id: 61,
        persian: "سه‌شنبه",
        pronunciation: "Seshanbe",
        english: "Tuesday",
        category: "days",
        difficulty: "beginner",
        audioUrl: "/audio/tuesday.wav"
      },
      {
        id: 62,
        persian: "چهارشنبه",
        pronunciation: "Chahārshanbe",
        english: "Wednesday",
        category: "days",
        difficulty: "beginner",
        audioUrl: "/audio/wednesday.wav"
      },
      {
        id: 63,
        persian: "پنج‌شنبه",
        pronunciation: "Panjshanbe",
        english: "Thursday",
        category: "days",
        difficulty: "beginner",
        audioUrl: "/audio/thursday.wav"
      },
      {
        id: 64,
        persian: "جمعه",
        pronunciation: "Jom'e",
        english: "Friday",
        category: "days",
        difficulty: "beginner",
        audioUrl: "/audio/friday.wav"
      },
      {
        id: 65,
        persian: "امروز",
        pronunciation: "Emrooz",
        english: "Today",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/today.wav"
      },
      {
        id: 66,
        persian: "فردا",
        pronunciation: "Fardā",
        english: "Tomorrow",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/tomorrow.wav"
      },
      {
        id: 67,
        persian: "دیروز",
        pronunciation: "Dirooz",
        english: "Yesterday",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/yesterday.wav"
      },
      {
        id: 68,
        persian: "هر روز",
        pronunciation: "Har rooz",
        english: "Every day",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/every_day.wav"
      },
      {
        id: 69,
        persian: "تعطیلات",
        pronunciation: "Ta'tilāt",
        english: "Holidays/Weekend",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/holidays_weekend.wav"
      },
      {
        id: 70,
        persian: "امروز چه روزی‌ه؟",
        pronunciation: "Emrooz che rooziye?",
        english: "What day is today?",
        category: "questions",
        difficulty: "beginner",
        audioUrl: "/audio/what_day_is_today.wav"
      },
      // Mini-Course 3: Months, Seasons, and the Persian Calendar
      {
        id: 71,
        persian: "فروردین",
        pronunciation: "Farvardin",
        english: "March-April (First month of Persian calendar)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/farvardin.wav"
      },
      {
        id: 72,
        persian: "اردیبهشت",
        pronunciation: "Ordibehesht",
        english: "April-May (Second month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/ordibehesht.wav"
      },
      {
        id: 73,
        persian: "خرداد",
        pronunciation: "Khordād",
        english: "May-June (Third month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/khordad.wav"
      },
      {
        id: 74,
        persian: "تیر",
        pronunciation: "Tir",
        english: "June-July (Fourth month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/tir.wav"
      },
      {
        id: 75,
        persian: "مرداد",
        pronunciation: "Mordād",
        english: "July-August (Fifth month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/mordad.wav"
      },
      {
        id: 76,
        persian: "شهریور",
        pronunciation: "Shahrivar",
        english: "August-September (Sixth month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/shahrivar.wav"
      },
      {
        id: 77,
        persian: "مهر",
        pronunciation: "Mehr",
        english: "September-October (Seventh month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/mehr.wav"
      },
      {
        id: 78,
        persian: "آبان",
        pronunciation: "Ābān",
        english: "October-November (Eighth month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/abban.wav"
      },
      {
        id: 79,
        persian: "آذر",
        pronunciation: "Āzar",
        english: "November-December (Ninth month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/azar.wav"
      },
      {
        id: 80,
        persian: "دی",
        pronunciation: "Dey",
        english: "December-January (Tenth month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/dey.wav"
      },
      {
        id: 81,
        persian: "بهمن",
        pronunciation: "Bahman",
        english: "January-February (Eleventh month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/bahman.wav"
      },
      {
        id: 82,
        persian: "اسفند",
        pronunciation: "Esfand",
        english: "February-March (Twelfth month)",
        category: "months",
        difficulty: "beginner",
        audioUrl: "/audio/esfand.wav"
      },
      {
        id: 83,
        persian: "بهار",
        pronunciation: "Bahār",
        english: "Spring",
        category: "seasons",
        difficulty: "beginner",
        audioUrl: "/audio/bahar.wav"
      },
      {
        id: 84,
        persian: "تابستان",
        pronunciation: "Tābestān",
        english: "Summer",
        category: "seasons",
        difficulty: "beginner",
        audioUrl: "/audio/tabestan.wav"
      },
      {
        id: 85,
        persian: "پاییز",
        pronunciation: "Pāyiz",
        english: "Autumn/Fall",
        category: "seasons",
        difficulty: "beginner",
        audioUrl: "/audio/payiz.wav"
      },
      {
        id: 86,
        persian: "زمستان",
        pronunciation: "Zemestān",
        english: "Winter",
        category: "seasons",
        difficulty: "beginner",
        audioUrl: "/audio/zemestan.wav"
      },
      {
        id: 87,
        persian: "ماه",
        pronunciation: "Māh",
        english: "Month",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/month.wav"
      },
      {
        id: 88,
        persian: "فصل",
        pronunciation: "Fasl",
        english: "Season",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/fasl.wav"
      },
      {
        id: 89,
        persian: "تولد",
        pronunciation: "Tavallod",
        english: "Birthday",
        category: "general",
        difficulty: "beginner",
        audioUrl: "/audio/tavallod.wav"
      },
      {
        id: 90,
        persian: "تقویم",
        pronunciation: "Taqvim",
        english: "Calendar",
        category: "general",
        difficulty: "beginner",
        audioUrl: "/audio/taqvim.wav"
      },
      {
        id: 91,
        persian: "اول",
        pronunciation: "Avval",
        english: "First",
        category: "ordinal",
        difficulty: "beginner",
        audioUrl: "/audio/first.wav"
      },
      {
        id: 92,
        persian: "دوم",
        pronunciation: "Dovvom",
        english: "Second",
        category: "ordinal",
        difficulty: "beginner",
        audioUrl: "/audio/second.wav"
      },
      {
        id: 93,
        persian: "سوم",
        pronunciation: "Sevvom",
        english: "Third",
        category: "ordinal",
        difficulty: "beginner",
        audioUrl: "/audio/third.wav"
      },
      // Mini-Course 4: Time Expressions & Temporal Language
      {
        id: 94,
        persian: "بعداً",
        pronunciation: "Ba'dan",
        english: "Later",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/later.wav"
      },
      {
        id: 95,
        persian: "زودتر",
        pronunciation: "Zoodtar",
        english: "Sooner/Earlier",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/sooner.wav"
      },
      {
        id: 96,
        persian: "الان",
        pronunciation: "Alān",
        english: "Now",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/now.wav"
      },
      {
        id: 97,
        persian: "سه روز پیش",
        pronunciation: "Se rooz pish",
        english: "Three days ago",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/three_days_ago.wav"
      },
      {
        id: 98,
        persian: "تازه رسیدم",
        pronunciation: "Tāze residam",
        english: "I just got here!",
        category: "expressions",
        difficulty: "beginner",
        audioUrl: "/audio/i_just_got_here.wav"
      },
      {
        id: 99,
        persian: "هفته پیش",
        pronunciation: "Hafte pish",
        english: "Last week",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/last_week.wav"
      },
      {
        id: 100,
        persian: "هفته بعد",
        pronunciation: "Hafte bad",
        english: "Next week",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/next_week.wav"
      },
      {
        id: 101,
        persian: "همیشه",
        pronunciation: "Hamishe",
        english: "Always",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/always.wav"
      },
      {
        id: 102,
        persian: "گاهی",
        pronunciation: "Gāhi",
        english: "Sometimes",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/sometimes.wav"
      },
      // Mini-Course 5: Dates, Appointments, and Planning Events
      {
        id: 103,
        persian: "قرار",
        pronunciation: "Qarār",
        english: "Appointment/Meeting",
        category: "planning",
        difficulty: "beginner",
        audioUrl: "/audio/appointment.wav"
      },
      {
        id: 104,
        persian: "تاریخ",
        pronunciation: "Tārikh",
        english: "Date",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/date.wav"
      },
      {
        id: 105,
        persian: "ساعت",
        pronunciation: "Sā'at",
        english: "Hour/Time",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/sahat.wav"
      },
      {
        id: 106,
        persian: "ملاقات",
        pronunciation: "Molāqāt",
        english: "Meeting/Appointment",
        category: "planning",
        difficulty: "beginner",
        audioUrl: "/audio/molagat.wav"
      },
      {
        id: 107,
        persian: "امروز چه تاریخی‌ه؟",
        pronunciation: "Emrooz che tārikhiye?",
        english: "What's the date today?",
        category: "questions",
        difficulty: "beginner",
        audioUrl: "/audio/whats_the_date_today.wav"
      },
      {
        id: 108,
        persian: "کی قرار داریم؟",
        pronunciation: "Key qarār dārim?",
        english: "When is our meeting?",
        category: "questions",
        difficulty: "beginner",
        audioUrl: "/audio/when_is_our_meeting.wav"
      },
      {
        id: 109,
        persian: "سه‌شنبه ساعت سه",
        pronunciation: "Seshanbe sā'at se",
        english: "Tuesday at 3 o'clock",
        category: "time",
        difficulty: "beginner",
        audioUrl: "/audio/tuesday_at_3_o_clock.wav"
      },
      {
        id: 110,
        persian: "جمعه تغییر بدیم؟",
        pronunciation: "Jom'e taghir bedim?",
        english: "Can we change it to Friday?",
        category: "planning",
        difficulty: "beginner",
        audioUrl: "/audio/can_we_change_it_to_friday.wav"
      }
    ],
    quiz: [
      {
        id: 16,
        question: "What is the first month of the Persian calendar?",
        type: "multiple-choice",
        options: [
          "فروردین",
          "اردیبهشت",
          "خرداد",
          "تیر"
        ],
        correctAnswer: "فروردین",
        explanation: "فروردین (Farvardin) is the first month of the Persian calendar, corresponding to March-April"
      },
      {
        id: 17,
        question: "How do you say 'Monday' in Persian?",
        type: "multiple-choice",
        options: [
          "شنبه",
          "یکشنبه",
          "دوشنبه",
          "سه‌شنبه"
        ],
        correctAnswer: "دوشنبه",
        explanation: "دوشنبه (Doshanbe) means 'Monday' in Persian"
      },
      {
        id: 18,
        question: "What does 'پنج' mean?",
        type: "translation",
        correctAnswer: "Five",
        explanation: "پنج (Panj) means 'Five' in Persian"
      },
      {
        id: 19,
        question: "Translate: 'Friday'",
        type: "translation",
        correctAnswer: "جمعه",
        explanation: "جمعه (Jom'e) means 'Friday' in Persian"
      },
      {
        id: 20,
        question: "Which month corresponds to September-October in the Persian calendar?",
        type: "multiple-choice",
        options: [
          "شهریور",
          "مهر",
          "آبان",
          "آذر"
        ],
        correctAnswer: "مهر",
        explanation: "مهر (Mehr) corresponds to September-October in the Persian calendar"
      },
      {
        id: 21,
        question: "How do you say 'Zero' in Persian?",
        type: "translation",
        correctAnswer: "صفر",
        explanation: "صفر (Sefr) means 'Zero' in Persian"
      },
      {
        id: 22,
        question: "What does 'بهار' mean?",
        type: "translation",
        correctAnswer: "Spring",
        explanation: "بهار (Bahār) means 'Spring' in Persian"
      },
      {
        id: 23,
        question: "How do you say 'Today' in Persian?",
        type: "translation",
        correctAnswer: "امروز",
        explanation: "امروز (Emrooz) means 'Today' in Persian"
      },
      {
        id: 24,
        question: "What does 'قرار' mean?",
        type: "translation",
        correctAnswer: "Appointment/Meeting",
        explanation: "قرار (Qarār) means 'Appointment' or 'Meeting' in Persian"
      },
      {
        id: 25,
        question: "How do you say 'Thirty' in Persian?",
        type: "translation",
        correctAnswer: "سی",
        explanation: "سی (Si) means 'Thirty' in Persian"
      },
      {
        id: 26,
        question: "What does 'فردا' mean?",
        type: "translation",
        correctAnswer: "Tomorrow",
        explanation: "فردا (Fardā) means 'Tomorrow' in Persian"
      },
      {
        id: 27,
        question: "How do you say 'Winter' in Persian?",
        type: "translation",
        correctAnswer: "زمستان",
        explanation: "زمستان (Zemestān) means 'Winter' in Persian"
      },
      {
        id: 28,
        question: "What does 'چند؟' mean?",
        type: "translation",
        correctAnswer: "How many?",
        explanation: "چند؟ (Chand?) means 'How many?' in Persian"
      },
      {
        id: 29,
        question: "How do you say 'Birthday' in Persian?",
        type: "translation",
        correctAnswer: "تولد",
        explanation: "تولد (Tavallod) means 'Birthday' in Persian"
      },
      {
        id: 30,
        question: "What does 'همیشه' mean?",
        type: "translation",
        correctAnswer: "Always",
        explanation: "همیشه (Hamishe) means 'Always' in Persian"
      }
    ]
  },
  {
    id: "can-talk-about-your-hobbies",
    title: "Can Talk About Your Hobbies",
    persianTitle: "می‌توانید درباره‌ی سرگرمی‌هایتان صحبت کنید",
    description: "Learn to talk about your hobbies and ask others about theirs.",
    focus: "Discussing hobbies and interests",
    goal: "Be able to ask and answer questions about hobbies in Persian",
    icon: "🎨",
    questions: [
      {
        id: 2001,
        persian: "سرگرمی مورد علاقه‌ات چیه؟",
        pronunciation: "Sargarmi-ye mored-e alaghe-at chiye?",
        english: "What is your favorite hobby?",
        category: "hobbies",
        difficulty: "beginner",
        audioUrl: "/audio/what_is_your_favorite_hobby.mp3"
      },
      {
        id: 2002,
        persian: "ورزش می‌کنی؟",
        pronunciation: "Varzesh mikoni?",
        english: "Do you play sports?",
        category: "hobbies",
        difficulty: "beginner",
        audioUrl: "/audio/do_you_play_sports.mp3"
      },
      {
        id: 2003,
        persian: "چه سازی بلدی؟",
        pronunciation: "Che sāzi baladi?",
        english: "What instrument do you play?",
        category: "hobbies",
        difficulty: "beginner",
        audioUrl: "/audio/what_instrument_do_you_play.mp3"
      },
      {
        id: 2004,
        persian: "کتاب خوندن دوست داری؟",
        pronunciation: "Ketāb khoondan doost dari?",
        english: "Do you like reading books?",
        category: "hobbies",
        difficulty: "beginner",
        audioUrl: "/audio/do_you_like_reading_books.mp3"
      },
      {
        id: 2005,
        persian: "فیلم دیدن رو دوست داری؟",
        pronunciation: "Film didan ro doost dari?",
        english: "Do you like watching movies?",
        category: "hobbies",
        difficulty: "beginner",
        audioUrl: "/audio/do_you_like_watching_movies.mp3"
      }
    ],
    quiz: [
      {
        id: 1,
        question: "How do you ask 'What is your favorite hobby?' in Persian?",
        type: "multiple-choice",
        options: [
          "سرگرمی مورد علاقه‌ات چیه؟",
          "ورزش می‌کنی؟",
          "چه سازی بلدی؟",
          "کتاب خوندن دوست داری؟"
        ],
        correctAnswer: "سرگرمی مورد علاقه‌ات چیه؟",
        explanation: "سرگرمی مورد علاقه‌ات چیه؟ (Sargarmi-ye mored-e alaghe-at chiye?) is the correct way to ask 'What is your favorite hobby?'"
      }
    ]
  },
  {
    id: 101,
    title: "Persian for Business Meetings",
    persianTitle: "فارسی برای جلسات کاری",
    description: "Master key phrases for professional and business settings.",
    focus: "Business communication",
    goal: "Participate in meetings and negotiations",
    icon: "💼",
    questions: [
      { id: 1001, persian: "جلسه چه ساعتی شروع می‌شود؟", pronunciation: "Jalase che sā'ati shoru' mishavad?", english: "What time does the meeting start?", category: "business", difficulty: "advanced", audioUrl: "/audio/what_time_does_the_meeting_start.wav" },
      { id: 1002, persian: "می‌توانیم قرارداد را مرور کنیم؟", pronunciation: "Mitavānim gharārdād rā morur konim?", english: "Can we review the contract?", category: "business", difficulty: "advanced", audioUrl: "/audio/can_we_review_the_contract.wav" }
    ],
    quiz: []
  },
  {
    id: 102,
    title: "Persian for Travel Emergencies",
    persianTitle: "فارسی برای شرایط اضطراری سفر",
    description: "Essential phrases for handling emergencies while traveling.",
    focus: "Travel emergencies",
    goal: "Get help and stay safe in emergencies",
    icon: "🚨",
    questions: [
      { id: 1003, persian: "کمک! کیفم را گم کرده‌ام.", pronunciation: "Komak! Kifam rā gom karde-am.", english: "Help! I lost my bag.", category: "emergency", difficulty: "advanced", audioUrl: "/audio/help_i_lost_my_bag.wav" },
      { id: 1004, persian: "شماره پلیس چند است؟", pronunciation: "Shomāre-ye polis chand ast?", english: "What is the police number?", category: "emergency", difficulty: "advanced", audioUrl: "/audio/what_is_the_police_number.wav" }
    ],
    quiz: []
  },
  {
    id: 103,
    title: "Persian for Medical Visits",
    persianTitle: "فارسی برای ویزیت پزشکی",
    description: "Communicate effectively during doctor or hospital visits.",
    focus: "Medical situations",
    goal: "Describe symptoms and understand instructions",
    icon: "🩺",
    questions: [
      { id: 1005, persian: "حالم خوب نیست.", pronunciation: "Hālam khoob nist.", english: "I don't feel well.", category: "medical", difficulty: "advanced", audioUrl: "/audio/i_dont_feel_well.wav" },
      { id: 1006, persian: "آیا می‌توانم دارو بگیرم؟", pronunciation: "Āyā mitavānam dāru begiram?", english: "Can I get medicine?", category: "medical", difficulty: "advanced", audioUrl: "/audio/can_i_get_medicine.wav" }
    ],
    quiz: []
  }
];

export const getAllQuestions = (): PersianQuestion[] => {
  return persianQuestionsData.flatMap(lesson => lesson.questions);
};

export const getLessonById = (id: string | number): Lesson | undefined => {
  return persianQuestionsData.find(lesson =>
    lesson.id === id ||
    lesson.id === Number(id) ||
    (lesson._id && lesson._id === id)
  );
};

export const getQuestionById = (id: number): PersianQuestion | undefined => {
  return getAllQuestions().find(question => question.id === id);
}; 